import { KnowledgeChannel } from './types';
import { KnowledgeLocale } from './knowledgeCopy';
import { resolveRtaImageSrc } from './knowledgeRtaImages';

function normTitle(value: string): string {
  return value
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/** Strip control chars and normalize SF Knowledge HTML for dangerouslySetInnerHTML. */
export function sanitizeKnowledgeHtml(
  html: string,
  locale: KnowledgeLocale,
  channel: KnowledgeChannel,
  articleTitle?: string
): string {
  if (!html) return '';

  const helpArticleBase = `/${locale}/help/article`;
  const heroArticleBase = `/${locale}/hero/article`;

  let normalized = html.replace(/[\x00-\x08\x0b\x0c\x0e-\x1f]/g, '');

  const sfHostPattern = '(?:https?:\\/\\/)?(?:urban\\.force\\.com|urbanco\\.my\\.site\\.com)';

  normalized = normalized
    .replace(new RegExp(`${sfHostPattern}/help/s/article/`, 'gi'), `${helpArticleBase}/`)
    .replace(new RegExp(`${sfHostPattern}/hero/s/article/`, 'gi'), `${heroArticleBase}/`)
    .replace(/urban\.force\.com\/help\/s\/article\//gi, `${helpArticleBase}/`)
    .replace(/urban\.force\.com\/hero\/s\/article\//gi, `${heroArticleBase}/`)
    .replace(/urbanco\.my\.site\.com\/help\/s\/article\//gi, `${helpArticleBase}/`)
    .replace(/urbanco\.my\.site\.com\/hero\/s\/article\//gi, `${heroArticleBase}/`)
    .replace(/urban\.force\.com\/help\/s/gi, `/${locale}/help`)
    .replace(/urban\.force\.com\/hero\/s/gi, `/${locale}/hero`)
    .replace(/urbanco\.my\.site\.com\/help\/s/gi, `/${locale}/help`)
    .replace(/urbanco\.my\.site\.com\/hero\/s/gi, `/${locale}/hero`)
    .replace(/href="\/article\//gi, `href="/${locale}/${channel}/article/`)
    .replace(/href="\/hero\/s\/article\//gi, `href="${heroArticleBase}/`)
    .replace(/href="\/help\/s\/article\//gi, `href="${helpArticleBase}/`);

  normalized = unwrapCloudinaryImages(normalized);
  normalized = resolveMappedRtaImages(normalized);
  normalized = normalizeKnowledgeImages(normalized);
  normalized = normalizeSalesforceListHtml(normalized);
  normalized = normalizeInlineTypography(normalized);
  normalized = normalizeHelpArticlePresentation(normalized);

  if (articleTitle) {
    normalized = removeDuplicateTitle(normalized, articleTitle);
  }

  return normalized.trim();
}

/** Use Cloudinary href when Salesforce rich-text images only reference rtaImage. */
function unwrapCloudinaryImages(html: string): string {
  return html
    .replace(
      /<a\b[^>]*\bhref="(https?:\/\/res\.cloudinary\.com[^"]+)"[^>]*>\s*(<img\b[^>]*\bsrc="[^"]*rtaImage[^"]*"[^>]*>)\s*<\/a>/gi,
      (_match, cloudinaryUrl: string, imgTag: string) =>
        imgTag.replace(/src="[^"]*rtaImage[^"]*"/i, `src="${cloudinaryUrl}"`)
    )
    .replace(
      /<img\b([^>]*)\bsrc="[^"]*\/hero\/servlet\/rtaImage[^"]*"([^>]*)>/gi,
      (match, before, after, offset, whole) => {
        const preceding = whole.slice(Math.max(0, offset - 400), offset);
        const anchorMatch = preceding.match(
          /<a\b[^>]*\bhref="(https?:\/\/res\.cloudinary\.com[^"]+)"[^>]*>\s*$/i
        );
        if (!anchorMatch) {
          return match;
        }
        return `<img${before}src="${anchorMatch[1]}"${after}>`;
      }
    );
}

/** Replace broken Salesforce servlet URLs with hosted static assets. */
function resolveMappedRtaImages(html: string): string {
  return html.replace(
    /<img\b([^>]*)\bsrc="[^"]*\/hero\/servlet\/rtaImage[^"]*refid=([^"&]+)[^"]*"([^>]*)>/gi,
    (_match, before, refid, after) => {
      const mapped = resolveRtaImageSrc(refid);
      if (!mapped) {
        return _match;
      }
      return `<img${before}src="${mapped}"${after}>`;
    }
  );
}

/** Fix hosted knowledge images: drop SF dimensions and enable enlarge links. */
function normalizeKnowledgeImages(html: string): string {
  return html
    .replace(
      /<img\b([^>]*)\bsrc="(\/assets\/images\/knowledge\/[^"]+)"([^>]*)>/gi,
      (_match, before, src, after) => {
      const cleanedBefore = stripImageDimensions(before);
      const cleanedAfter = stripImageDimensions(after);
      const profileClass =
        src.includes('0EM3z000003UT6N') ||
        src.includes('0EM3z000003UT6X') ||
        src.includes('0EM3z000003Utfz') ||
        src.includes('0EM3z000003Utg4')
          ? ' class="knowledge-profile-example"'
          : '';
      return `<img${profileClass}${cleanedBefore}src="${src}"${cleanedAfter}>`;
      }
    )
    .replace(
      /<img\b([^>]*)\bsrc="(\/assets\/images\/knowledge\/[^"]+)"([^>]*)>\s*(?:<br\s*\/?>)?\s*<i>\s*Click on the above image for a bigger view\s*<\/i>/gi,
      (_match, before, src, after) =>
        `<a href="${src}" target="_blank" rel="noopener noreferrer" class="knowledge-image-enlarge"><img${before}src="${src}"${after}></a>` +
        `<p><a href="${src}" target="_blank" rel="noopener noreferrer"><em>Click on the above image for a bigger view</em></a></p>`
    );
}

function stripImageDimensions(attrs: string): string {
  return attrs
    .replace(/\s*(?:width|height)="[^"]*"/gi, '')
    .replace(/\s*style="[^"]*(?:width|height)[^"]*"/gi, '');
}

/** Strip legacy Salesforce inline typography so hero articles use site fonts. */
function normalizeInlineTypography(html: string): string {
  return html
    .replace(/font-family:\s*georgia,\s*palatino,\s*serif;?/gi, '')
    .replace(/font-family:\s*arial,\s*helvetica,\s*sans-serif;?/gi, '')
    .replace(/font-family:\s*Arial,\s*sans-serif;?/gi, '')
    .replace(/font-family:\s*Arial,\s*Helvetica,\s*sans-serif;?/gi, '')
    .replace(/font-family:\s*Roboto,\s*sans-serif;?/gi, '')
    .replace(/font-family:\s*'SF Pro Text';?/gi, '')
    .replace(/font-family:\s*SF Pro Text;?/gi, '')
    .replace(/font-size:\s*11pt;?/gi, '')
    .replace(/font-variant:\s*normal;?/gi, '')
    .replace(/white-space:\s*pre-wrap;?/gi, '')
    .replace(/style="\s*"/gi, '')
    .replace(/<span>\s*<\/span>/gi, '')
    .replace(/<strong>\s*<span[^>]*>\s*<\/span>\s*<\/strong>/gi, '');
}

function normalizeHelpArticlePresentation(html: string): string {
  return html
    .replace(/<p>\s*(?:&nbsp;|\u00a0|\s)*<\/p>/gi, '')
    .replace(/margin-top:\s*12pt;?/gi, '')
    .replace(/margin-bottom:\s*12pt;?/gi, '')
    .replace(/padding-bottom:\s*12pt;?/gi, '')
    .replace(/<p([^>]*)>\s*<strong><span[^>]*>([^<]+)<\/span><\/strong><\/span><\/p>/gi, '<h3>$2</h3>')
    .replace(/<p style="[^"]*">\s*<span[^>]*><strong><span[^>]*>([^<]+)<\/span><\/strong><\/span>\s*<\/p>/gi, '<h3>$1</h3>');
}

function removeDuplicateTitle(html: string, articleTitle: string): string {
  const target = normTitle(articleTitle);
  if (!target) {
    return html;
  }

  return html.replace(/<h[12][^>]*>[\s\S]*?<\/h[12]>/gi, (heading) =>
    normTitle(heading) === target ? '' : heading
  );
}

/** Salesforce often wraps list item text in <p>, which breaks bullet alignment. */
function normalizeSalesforceListHtml(html: string): string {
  let result = html;
  for (let i = 0; i < 5; i += 1) {
    const next = result.replace(
      /<li([^>]*)>\s*<p[^>]*>([\s\S]*?)<\/p>\s*<\/li>/gi,
      '<li$1>$2</li>'
    );
    if (next === result) {
      break;
    }
    result = next;
  }
  return result;
}
